import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TrophyIcon,
  ChartBarIcon,
  CalendarIcon,
  MapPinIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  StarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { TrophyIcon as TrophyIconSolid } from '@heroicons/react/24/solid';

const ResultsManagement = () => {
  const { sportId } = useParams();
  const [sport, setSport] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  const [filterResult, setFilterResult] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [formData, setFormData] = useState({
    gameDate: '',
    opponent: '',
    isHome: true,
    homeScore: '',
    awayScore: '',
    result: '',
    season: '2023-24',
    gameType: 'regular',
    location: '',
    attendance: '',
    highlights: '',
    injuries: '',
    notes: '',
    teamStats: {
      totalYards: '',
      passingYards: '',
      rushingYards: '',
      turnovers: '',
      penalties: '',
      timeOfPossession: ''
    },
    playerStats: []
  });

  useEffect(() => {
    fetchSportData();
    fetchResults();
  }, [sportId]);

  const fetchSportData = async () => {
    try {
      const response = await fetch(`/api/sports/id/${sportId}`);
      if (response.ok) {
        const data = await response.json();
        setSport(data.sport);
      } else {
        // Mock data fallback
        setSport({
          id: sportId,
          name: 'Football',
          slug: 'football',
          coachName: 'Coach Johnson'
        });
      }
    } catch (error) {
      console.error('Error fetching sport:', error);
    }
  };

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/game-results/sport/${sportId}`);
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
      } else {
        // Mock data fallback
        loadMockResults();
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      loadMockResults();
    } finally {
      setLoading(false);
    }
  };

  const loadMockResults = () => {
    const mockResults = [
      {
        id: 1,
        gameDate: '2024-01-15',
        opponent: 'Central High',
        isHome: true,
        homeScore: 28,
        awayScore: 14,
        result: 'win',
        season: '2023-24',
        gameType: 'regular',
        location: 'East End Prep Stadium',
        attendance: 1250,
        highlights: 'Johnson rushed for 180 yards and 2 TDs',
        injuries: 'None reported',
        notes: 'Dominant performance in all phases',
        teamStats: {
          totalYards: 425,
          passingYards: 245,
          rushingYards: 180,
          turnovers: 1,
          penalties: 6,
          timeOfPossession: '32:15'
        },
        playerStats: [
          { playerId: 1, playerName: 'Mike Johnson', position: 'RB', stats: { rushingYards: 180, touchdowns: 2 } },
          { playerId: 2, playerName: 'Tom Wilson', position: 'QB', stats: { passingYards: 245, touchdowns: 2 } }
        ]
      },
      {
        id: 2,
        gameDate: '2024-01-08',
        opponent: 'West High',
        isHome: false,
        homeScore: 21,
        awayScore: 24,
        result: 'win',
        season: '2023-24',
        gameType: 'regular',
        location: 'West High Stadium',
        attendance: 980,
        highlights: 'Last-minute field goal for the win',
        injuries: 'Smith - minor ankle sprain',
        notes: 'Comeback victory in final quarter',
        teamStats: {
          totalYards: 312,
          passingYards: 198,
          rushingYards: 114,
          turnovers: 2,
          penalties: 8,
          timeOfPossession: '28:45'
        },
        playerStats: [
          { playerId: 3, playerName: 'Jake Smith', position: 'K', stats: { fieldGoals: 2, extraPoints: 3 } },
          { playerId: 2, playerName: 'Tom Wilson', position: 'QB', stats: { passingYards: 198, touchdowns: 1 } }
        ]
      },
      {
        id: 3,
        gameDate: '2024-01-01',
        opponent: 'North Academy',
        isHome: true,
        homeScore: 17,
        awayScore: 21,
        result: 'loss',
        season: '2023-24',
        gameType: 'playoff',
        location: 'East End Prep Stadium',
        attendance: 1500,
        highlights: 'Strong defensive effort despite loss',
        injuries: 'Brown - concussion protocol',
        notes: 'Season-ending playoff loss',
        teamStats: {
          totalYards: 289,
          passingYards: 156,
          rushingYards: 133,
          turnovers: 3,
          penalties: 5,
          timeOfPossession: '26:30'
        },
        playerStats: [
          { playerId: 4, playerName: 'Chris Brown', position: 'LB', stats: { tackles: 12, sacks: 2 } },
          { playerId: 2, playerName: 'Tom Wilson', position: 'QB', stats: { passingYards: 156, touchdowns: 1, interceptions: 2 } }
        ]
      }
    ];
    setResults(mockResults);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('teamStats.')) {
      const statName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        teamStats: {
          ...prev.teamStats,
          [statName]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const dataToSend = {
        ...formData,
        sportId: parseInt(sportId),
        homeScore: parseInt(formData.homeScore),
        awayScore: parseInt(formData.awayScore),
        attendance: formData.attendance ? parseInt(formData.attendance) : null
      };

      const url = editingResult 
        ? `/api/game-results/${editingResult.id}`
        : '/api/game-results';
      
      const method = editingResult ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        await fetchResults();
        resetForm();
        setShowAddModal(false);
        setEditingResult(null);
      } else {
        console.error('Error saving result');
      }
    } catch (error) {
      console.error('Error saving result:', error);
    }
  };

  const handleEdit = (result) => {
    setEditingResult(result);
    setFormData({
      gameDate: result.gameDate || '',
      opponent: result.opponent || '',
      isHome: result.isHome !== undefined ? result.isHome : true,
      homeScore: result.homeScore?.toString() || '',
      awayScore: result.awayScore?.toString() || '',
      result: result.result || '',
      season: result.season || '2023-24',
      gameType: result.gameType || 'regular',
      location: result.location || '',
      attendance: result.attendance?.toString() || '',
      highlights: result.highlights || '',
      injuries: result.injuries || '',
      notes: result.notes || '',
      teamStats: result.teamStats || {
        totalYards: '',
        passingYards: '',
        rushingYards: '',
        turnovers: '',
        penalties: '',
        timeOfPossession: ''
      },
      playerStats: result.playerStats || []
    });
    setShowAddModal(true);
  };

  const handleDelete = async (resultId) => {
    if (window.confirm('Are you sure you want to delete this game result?')) {
      try {
        const response = await fetch(`/api/game-results/${resultId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (response.ok) {
          await fetchResults();
        } else {
          console.error('Error deleting result');
        }
      } catch (error) {
        console.error('Error deleting result:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      gameDate: '',
      opponent: '',
      isHome: true,
      homeScore: '',
      awayScore: '',
      result: '',
      season: '2023-24',
      gameType: 'regular',
      location: '',
      attendance: '',
      highlights: '',
      injuries: '',
      notes: '',
      teamStats: {
        totalYards: '',
        passingYards: '',
        rushingYards: '',
        turnovers: '',
        penalties: '',
        timeOfPossession: ''
      },
      playerStats: []
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'win':
        return 'text-green-600 bg-green-100';
      case 'loss':
        return 'text-red-600 bg-red-100';
      case 'tie':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getGameTypeColor = (gameType) => {
    switch (gameType) {
      case 'playoff':
        return 'text-purple-600 bg-purple-100';
      case 'championship':
        return 'text-yellow-600 bg-yellow-100';
      case 'scrimmage':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const calculateRecord = () => {
    const wins = results.filter(r => r.result === 'win').length;
    const losses = results.filter(r => r.result === 'loss').length;
    const ties = results.filter(r => r.result === 'tie').length;
    return { wins, losses, ties };
  };

  const filteredResults = results.filter(result => {
    const matchesSearch = 
      result.opponent.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSeason = !filterSeason || result.season === filterSeason;
    const matchesResult = !filterResult || result.result === filterResult;
    
    return matchesSearch && matchesSeason && matchesResult;
  });

  const sortedResults = filteredResults.sort((a, b) => 
    new Date(b.gameDate) - new Date(a.gameDate)
  );

  const record = calculateRecord();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/sports"
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {sport?.name} Results Management
              </h1>
              <p className="text-gray-600 mt-1">
                Track game results, statistics, and team performance
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingResult(null);
              setShowAddModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Result
          </button>
        </div>
      </div>

      {/* Season Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <TrophyIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Wins</p>
              <p className="text-2xl font-bold text-gray-900">{record.wins}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <ChartBarIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Losses</p>
              <p className="text-2xl font-bold text-gray-900">{record.losses}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <StarIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ties</p>
              <p className="text-2xl font-bold text-gray-900">{record.ties}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <CalendarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Record</p>
              <p className="text-2xl font-bold text-gray-900">
                {record.wins}-{record.losses}{record.ties > 0 && `-${record.ties}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search opponents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <select
            value={filterSeason}
            onChange={(e) => setFilterSeason(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">All Seasons</option>
            <option value="2023-24">2023-24</option>
            <option value="2022-23">2022-23</option>
            <option value="2021-22">2021-22</option>
          </select>
          
          <select
            value={filterResult}
            onChange={(e) => setFilterResult(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">All Results</option>
            <option value="win">Wins</option>
            <option value="loss">Losses</option>
            <option value="tie">Ties</option>
          </select>
          
          <div className="flex items-center text-sm text-gray-600">
            <FunnelIcon className="h-5 w-5 mr-2" />
            {sortedResults.length} of {results.length} games
          </div>
        </div>
      </div>

      {/* Results List */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Game Results</h2>
        </div>
        
        {sortedResults.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {sortedResults.map((result) => {
              const ourScore = result.isHome ? result.homeScore : result.awayScore;
              const theirScore = result.isHome ? result.awayScore : result.homeScore;
              
              return (
                <div key={result.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-full ${getResultColor(result.result)}`}>
                        <TrophyIcon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {result.isHome ? 'vs.' : 'at'} {result.opponent}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getResultColor(result.result)}`}>
                            {result.result.charAt(0).toUpperCase() + result.result.slice(1)}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getGameTypeColor(result.gameType)}`}>
                            {result.gameType.charAt(0).toUpperCase() + result.gameType.slice(1)}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm text-gray-600 mt-2">
                          <span className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDate(result.gameDate)}
                          </span>
                          {result.location && (
                            <span className="flex items-center">
                              <MapPinIcon className="h-4 w-4 mr-1" />
                              {result.location}
                            </span>
                          )}
                          {result.attendance && (
                            <span className="flex items-center">
                              <UserIcon className="h-4 w-4 mr-1" />
                              {result.attendance.toLocaleString()} attendance
                            </span>
                          )}
                        </div>
                        
                        <div className="mt-3">
                          <div className="text-2xl font-bold text-gray-900">
                            East End Prep {ourScore} - {result.opponent} {theirScore}
                          </div>
                        </div>
                        
                        {result.highlights && (
                          <p className="text-sm text-gray-600 mt-2">
                            <strong>Highlights:</strong> {result.highlights}
                          </p>
                        )}
                        
                        {result.teamStats && Object.values(result.teamStats).some(stat => stat) && (
                          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4 text-xs text-gray-500">
                            {result.teamStats.totalYards && (
                              <div>
                                <strong>Total Yards:</strong> {result.teamStats.totalYards}
                              </div>
                            )}
                            {result.teamStats.passingYards && (
                              <div>
                                <strong>Passing:</strong> {result.teamStats.passingYards}
                              </div>
                            )}
                            {result.teamStats.rushingYards && (
                              <div>
                                <strong>Rushing:</strong> {result.teamStats.rushingYards}
                              </div>
                            )}
                            {result.teamStats.turnovers && (
                              <div>
                                <strong>Turnovers:</strong> {result.teamStats.turnovers}
                              </div>
                            )}
                            {result.teamStats.penalties && (
                              <div>
                                <strong>Penalties:</strong> {result.teamStats.penalties}
                              </div>
                            )}
                            {result.teamStats.timeOfPossession && (
                              <div>
                                <strong>TOP:</strong> {result.teamStats.timeOfPossession}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {result.injuries && (
                          <p className="text-xs text-red-600 mt-2">
                            <strong>Injuries:</strong> {result.injuries}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(result)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(result.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <TrophyIconSolid className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No game results found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Result Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingResult ? 'Edit Game Result' : 'Add Game Result'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingResult(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Game Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">Game Information</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Game Date *</label>
                      <input
                        type="date"
                        name="gameDate"
                        value={formData.gameDate}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Opponent *</label>
                      <input
                        type="text"
                        name="opponent"
                        value={formData.opponent}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="isHome"
                          value="true"
                          checked={formData.isHome === true}
                          onChange={() => setFormData(prev => ({ ...prev, isHome: true }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          Home Game
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="isHome"
                          value="false"
                          checked={formData.isHome === false}
                          onChange={() => setFormData(prev => ({ ...prev, isHome: false }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          Away Game
                        </label>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {formData.isHome ? 'Our Score' : 'Away Score'} *
                        </label>
                        <input
                          type="number"
                          name={formData.isHome ? 'homeScore' : 'awayScore'}
                          value={formData.isHome ? formData.homeScore : formData.awayScore}
                          onChange={handleInputChange}
                          required
                          min="0"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {formData.isHome ? 'Their Score' : 'Home Score'} *
                        </label>
                        <input
                          type="number"
                          name={formData.isHome ? 'awayScore' : 'homeScore'}
                          value={formData.isHome ? formData.awayScore : formData.homeScore}
                          onChange={handleInputChange}
                          required
                          min="0"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Season</label>
                        <select
                          name="season"
                          value={formData.season}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="2023-24">2023-24</option>
                          <option value="2022-23">2022-23</option>
                          <option value="2021-22">2021-22</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Game Type</label>
                        <select
                          name="gameType"
                          value={formData.gameType}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="regular">Regular Season</option>
                          <option value="playoff">Playoff</option>
                          <option value="championship">Championship</option>
                          <option value="scrimmage">Scrimmage</option>
                          <option value="tournament">Tournament</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Additional Details */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">Additional Details</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Location</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Attendance</label>
                      <input
                        type="number"
                        name="attendance"
                        value={formData.attendance}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Highlights</label>
                      <textarea
                        name="highlights"
                        value={formData.highlights}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Key plays, standout performances, etc."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Injuries</label>
                      <textarea
                        name="injuries"
                        value={formData.injuries}
                        onChange={handleInputChange}
                        rows={2}
                        placeholder="Player injuries during the game"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notes</label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Additional game notes"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Team Statistics */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Team Statistics</h4>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Total Yards</label>
                      <input
                        type="number"
                        name="teamStats.totalYards"
                        value={formData.teamStats.totalYards}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Passing Yards</label>
                      <input
                        type="number"
                        name="teamStats.passingYards"
                        value={formData.teamStats.passingYards}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Rushing Yards</label>
                      <input
                        type="number"
                        name="teamStats.rushingYards"
                        value={formData.teamStats.rushingYards}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Turnovers</label>
                      <input
                        type="number"
                        name="teamStats.turnovers"
                        value={formData.teamStats.turnovers}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Penalties</label>
                      <input
                        type="number"
                        name="teamStats.penalties"
                        value={formData.teamStats.penalties}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Time of Possession</label>
                      <input
                        type="text"
                        name="teamStats.timeOfPossession"
                        value={formData.teamStats.timeOfPossession}
                        onChange={handleInputChange}
                        placeholder="MM:SS"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingResult(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingResult ? 'Update Result' : 'Add Result'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsManagement;